import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { approvalService } from '../../services/approvalService';
import { formatValidityMonthsLabel } from '../../utils/coachLicense';

const TABS = [
  { id: 'all', label: 'All Pending', icon: 'Inbox' },
  { id: 'regions', label: 'Regions', icon: 'MapPin' },
  { id: 'players', label: 'Player Licenses', icon: 'IdCard' },
  { id: 'coaches', label: 'Coaching Licenses', icon: 'GraduationCap' },
  { id: 'teams', label: 'Club Teams', icon: 'Shield' },
  { id: 'regionalTeams', label: 'Regional Teams', icon: 'Users' },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const typeLabel = (type) => {
  switch (type) {
    case 'region':
      return 'Region Association';
    case 'player':
      return 'Player License';
    case 'coach':
      return 'Coaching License';
    case 'team':
      return 'Club Team';
    case 'regional_team':
      return 'Regional Team';
    default:
      return type;
  }
};

const typeTone = (type) => {
  switch (type) {
    case 'region':
      return 'bg-teal-600/10 text-teal-700';
    case 'player':
      return 'bg-emerald-600/10 text-emerald-700';
    case 'coach':
      return 'bg-violet-600/10 text-violet-700';
    case 'team':
      return 'bg-blue-600/10 text-blue-700';
    case 'regional_team':
      return 'bg-amber-600/10 text-amber-700';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

const ApprovalsPage = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [pending, setPending] = useState({
    regions: [],
    players: [],
    coaches: [],
    teams: [],
    regionalTeams: [],
  });
  const [counts, setCounts] = useState({
    regions: 0,
    players: 0,
    coaches: 0,
    teams: 0,
    regionalTeams: 0,
    total: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [banner, setBanner] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const userRole = userProfile?.role;
  const isAdmin = userRole === 'admin';

  const breadcrumbItems = [
    { label: 'Admin Dashboard', path: '/admin-dashboard' },
    { label: 'Administration' },
    { label: 'Approvals' },
  ];

  const loadPending = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [items, nextCounts] = await Promise.all([
        approvalService.getAllPending(),
        approvalService.getPendingCounts(),
      ]);
      setPending(items);
      setCounts(nextCounts);
    } catch (err) {
      console.error('Failed to load approvals:', err);
      setError(err.message || 'Failed to load pending approvals.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      navigate('/admin-dashboard', { replace: true });
      return;
    }
    loadPending();
  }, [authLoading, isAdmin, loadPending, navigate]);

  const allItems = useMemo(
    () => [
      ...pending.regions,
      ...pending.players,
      ...pending.coaches,
      ...pending.teams,
      ...pending.regionalTeams,
    ].sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)),
    [pending]
  );

  const visibleItems = useMemo(() => {
    if (activeTab === 'regions') return pending.regions;
    if (activeTab === 'players') return pending.players;
    if (activeTab === 'coaches') return pending.coaches;
    if (activeTab === 'teams') return pending.teams;
    if (activeTab === 'regionalTeams') return pending.regionalTeams;
    return allItems;
  }, [activeTab, allItems, pending]);

  const tabCount = (tabId) => {
    if (tabId === 'all') return counts.total;
    if (tabId === 'regions') return counts.regions;
    if (tabId === 'players') return counts.players;
    if (tabId === 'coaches') return counts.coaches;
    if (tabId === 'teams') return counts.teams;
    if (tabId === 'regionalTeams') return counts.regionalTeams;
    return 0;
  };

  const handleDecision = async (item, decision) => {
    const actionVerb = decision === 'approve' ? 'Approve' : 'Reject';
    const confirmed = window.confirm(
      `${actionVerb} ${typeLabel(item.type)}: "${item.title}"?`
    );
    if (!confirmed) return;

    setBusyId(`${item.type}-${item.id}`);
    setBanner(null);
    try {
      await approvalService.decide(item, decision);
      setBanner({
        type: 'success',
        message: `${typeLabel(item.type)} ${decision === 'approve' ? 'approved' : 'rejected'} successfully.`,
      });
      await loadPending();
    } catch (err) {
      setBanner({
        type: 'error',
        message: err.message || `Failed to ${decision} request.`,
      });
    } finally {
      setBusyId(null);
    }
  };

  const renderMeta = (item) => {
    if (item.type === 'region') {
      return (
        <>
          {item.registrationNumber && <span>Reg #: {item.registrationNumber}</span>}
          {item.meta?.chairperson && <span>Chair: {item.meta.chairperson}</span>}
          {item.meta?.secretary && <span>Sec: {item.meta.secretary}</span>}
        </>
      );
    }
    if (item.type === 'player') {
      return (
        <>
          {item.licenseNumber && <span>License: {item.licenseNumber}</span>}
          {item.meta?.jerseyNumber != null && <span>#{item.meta.jerseyNumber}</span>}
          {item.meta?.position && <span>{String(item.meta.position).replace(/_/g, ' ')}</span>}
        </>
      );
    }
    if (item.type === 'coach') {
      return (
        <>
          {item.licenseNumber && <span>License: {item.licenseNumber}</span>}
          {item.meta?.grade && <span>Grade: {item.meta.grade}</span>}
          {item.meta?.region && <span>Region: {item.meta.region}</span>}
          {item.meta?.validityPeriod && (
            <span>Valid: {formatValidityMonthsLabel(item.meta.validityPeriod)}</span>
          )}
        </>
      );
    }
    if (item.type === 'team') {
      return (
        <>
          {item.meta?.manager && <span>Manager: {item.meta.manager}</span>}
          {item.meta?.venue && <span>{item.meta.venue}</span>}
          {item.meta?.teamStatus && <span>Team: {item.meta.teamStatus}</span>}
        </>
      );
    }
    return (
      <>
        {item.meta?.manager && <span>Manager: {item.meta.manager}</span>}
        {item.meta?.coach && <span>Coach: {item.meta.coach}</span>}
      </>
    );
  };

  if (authLoading || (!isAdmin && !error)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main
        className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        }`}
      >
        <div className="p-6 space-y-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <Breadcrumb items={breadcrumbItems} />
              <h1 className="text-2xl font-bold text-foreground mt-2">Approvals</h1>
              <p className="text-muted-foreground">
                Review and approve pending region, player license, coaching license, club team,
                and regional team registrations.
              </p>
            </div>
            <Button
              variant="outline"
              iconName="RefreshCcw"
              onClick={loadPending}
              loading={isLoading}
              disabled={isLoading}
            >
              Refresh
            </Button>
          </div>

          {banner && (
            <div
              className={`px-4 py-3 rounded-lg border text-sm ${
                banner.type === 'success'
                  ? 'bg-success/10 border-success/20 text-success'
                  : 'bg-destructive/10 border-destructive/20 text-destructive'
              }`}
            >
              {banner.message}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            {[
              { label: 'Total Pending', value: counts.total, icon: 'Inbox' },
              { label: 'Regions', value: counts.regions, icon: 'MapPin' },
              { label: 'Player Licenses', value: counts.players, icon: 'IdCard' },
              { label: 'Coaching Licenses', value: counts.coaches, icon: 'GraduationCap' },
              { label: 'Club Teams', value: counts.teams, icon: 'Shield' },
              { label: 'Regional Teams', value: counts.regionalTeams, icon: 'Users' },
            ].map((card) => (
              <div
                key={card.label}
                className="bg-card border border-border rounded-lg p-4 card-shadow"
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {card.label}
                  </p>
                  <Icon name={card.icon} size={16} className="text-muted-foreground" />
                </div>
                <p className="text-2xl font-bold text-foreground">{card.value}</p>
              </div>
            ))}
          </div>

          <div className="bg-card border border-border rounded-lg card-shadow">
            <div className="p-3 border-b border-border flex flex-wrap gap-2">
              {TABS.map((tab) => (
                <Button
                  key={tab.id}
                  size="sm"
                  variant={activeTab === tab.id ? 'default' : 'ghost'}
                  iconName={tab.icon}
                  iconPosition="left"
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                  <span className="ml-2 text-xs opacity-80">({tabCount(tab.id)})</span>
                </Button>
              ))}
            </div>

            <div className="divide-y divide-border">
              {isLoading && (
                <div className="py-16 text-center text-muted-foreground">
                  <Icon
                    name="Loader2"
                    size={32}
                    className="mx-auto mb-3 animate-spin text-primary"
                  />
                  Loading pending approvals...
                </div>
              )}

              {!isLoading && error && (
                <div className="py-12 text-center text-destructive space-y-2">
                  <Icon name="AlertTriangle" size={28} className="mx-auto" />
                  <p>{error}</p>
                  <Button variant="outline" size="sm" iconName="RefreshCcw" onClick={loadPending}>
                    Retry
                  </Button>
                </div>
              )}

              {!isLoading && !error && visibleItems.length === 0 && (
                <div className="py-16 text-center text-muted-foreground space-y-2">
                  <Icon name="CheckCircle2" size={32} className="mx-auto text-success" />
                  <p className="font-medium text-foreground">All caught up</p>
                  <p className="text-sm">No pending approvals in this category.</p>
                </div>
              )}

              {!isLoading &&
                !error &&
                visibleItems.map((item) => {
                  const key = `${item.type}-${item.id}`;
                  const isBusy = busyId === key;
                  return (
                    <div
                      key={key}
                      className="p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
                    >
                      <div className="min-w-0 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeTone(
                              item.type
                            )}`}
                          >
                            {typeLabel(item.type)}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-warning/10 text-warning capitalize">
                            {(item.status || 'pending').replace(/_/g, ' ')}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-foreground truncate">
                          {item.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">{item.subtitle}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span>Submitted: {formatDate(item.createdAt)}</span>
                          {item.email && <span>{item.email}</span>}
                          {item.mobile && <span>{item.mobile}</span>}
                          {renderMeta(item)}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="X"
                          disabled={isBusy}
                          onClick={() => handleDecision(item, 'reject')}
                        >
                          Reject
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          iconName="Check"
                          loading={isBusy}
                          disabled={isBusy}
                          onClick={() => handleDecision(item, 'approve')}
                        >
                          Approve
                        </Button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ApprovalsPage;
